import { EntityType } from '@frontend/common';
import { isUUID } from '@frontend/components/common';
import { ObjectDetailMapper } from '@frontend/components/detail';
import React, { useMemo } from 'react';

import { platformEntityTypeForKey } from '../platform-object-badge/platform-entity-type-by-key';
import { PlatformObjectBadge } from '../platform-object-badge/platform-object-badge.component';

type ValueRenderer<T> = (object: T) => React.ReactNode;

export interface PlatformDetailGridProps<T extends object> {
    object: T;
    /** Only these keys, in this order. Defaults to every key on the object. */
    order?: (keyof T | string)[];
    hidden?: (keyof T)[];
    keyOverwrite?: Map<keyof T | string, React.ReactNode | ((object: T) => React.ReactNode)>;
    valueOverwrite?: Map<keyof T | string, ValueRenderer<T>>;
    /** Entity type for fields whose name does not identify it (e.g. `owner` → USER). */
    entityTypeOverwrite?: Map<keyof T | string, EntityType>;
    cols?: number;
}

interface EntityReference {
    entity_id: string;
    entity_type: string;
}

function isEntityReference(value: unknown): value is EntityReference {
    const record = value as Partial<EntityReference> | null;
    return !!record && typeof record === 'object' && typeof record.entity_id === 'string' && typeof record.entity_type === 'string';
}

function renderBadges(references: { id: string; entityType: string }[]): React.ReactNode {
    return (
        <div className='flex flex-wrap gap-2'>
            {references.map((reference) => (
                <PlatformObjectBadge
                    key={reference.id}
                    id={reference.id}
                    entityType={reference.entityType}
                />
            ))}
        </div>
    );
}

/** Badge renderer for a field that references other entities, or undefined to keep ObjectDetailMapper's default. */
function entityBadgeRenderer<T extends object>(object: T, key: string, entityType: EntityType | undefined): ValueRenderer<T> | undefined {
    const value = (object as Record<string, unknown>)[key];

    if (typeof value === 'string' && key !== 'id' && isUUID(value)) {
        const type = key === 'entity_id' ? (object as Partial<EntityReference>).entity_type : entityType;
        return type ? () => renderBadges([{ id: value, entityType: type }]) : undefined;
    }
    if (isEntityReference(value)) {
        return () => renderBadges([{ id: value.entity_id, entityType: entityType ?? value.entity_type }]);
    }
    if (Array.isArray(value) && value.length > 0) {
        if (value.every(isEntityReference)) {
            return () => renderBadges(value.map((reference) => ({ id: reference.entity_id, entityType: reference.entity_type })));
        }
        if (entityType && value.every((item) => typeof item === 'string' && isUUID(item))) {
            return () => renderBadges((value as string[]).map((id) => ({ id, entityType })));
        }
    }
    return undefined;
}

/**
 * Platform detail field grid: ObjectDetailMapper formatting, but entity references render as
 * PlatformObjectBadge so they open on the platform detail stack.
 */
export const PlatformDetailGrid = <T extends object>(props: PlatformDetailGridProps<T>) => {
    const { object, order, valueOverwrite, entityTypeOverwrite } = props;

    const valueRenderers = useMemo(() => {
        const renderers = new Map<keyof T | string, ValueRenderer<T>>(valueOverwrite);
        const keys = (order ?? Object.keys(object)) as string[];
        for (const key of keys) {
            if (renderers.has(key)) {
                continue;
            }
            const renderer = entityBadgeRenderer(object, key, entityTypeOverwrite?.get(key) ?? platformEntityTypeForKey(key));
            if (renderer) {
                renderers.set(key, renderer);
            }
        }
        return renderers;
    }, [object, order, valueOverwrite, entityTypeOverwrite]);

    return (
        <ObjectDetailMapper
            object={object}
            order={order}
            hidden={props.hidden}
            keyOverwrite={props.keyOverwrite}
            valueOverwrite={valueRenderers}
            cols={props.cols}
            type='column'
        />
    );
};
